import _ from 'underscore';

import {omitNull} from 'common/utils';

export const LIMIT_TYPES = ['rides', 'cancellations', 'no_shows'];
export const TIMEFRAMES = ['service_day', 'hour'];

export const LIMIT_TYPE_LABELS = {
  rides: 'Rides',
  cancellations: 'Cancellations',
  no_shows: 'No-Shows',
};

// Labels for the timeframe dropdown, e.g. "service day(s)".
export const TIMEFRAME_LABELS = {
  service_day: 'service day(s)',
  hour: 'hour(s)',
};

const LIMIT_TYPE_NOUNS = {
  rides: {singular: 'ride', plural: 'rides'},
  cancellations: {singular: 'cancellation', plural: 'cancellations'},
  no_shows: {singular: 'no-show', plural: 'no-shows'},
};

const TIMEFRAME_UNITS = {
  service_day: {singular: 'service day', plural: 'service days'},
  hour: {singular: 'hour', plural: 'hours'},
};

export default class ServiceUserLimit {
  /* eslint-disable camelcase */
  constructor({
    _id,
    limit_type,
    timeframe,
    timeframe_value,
    limit_value,
  } = {}) {
    this.limit_type = limit_type || LIMIT_TYPES[0];
    this.timeframe = timeframe || TIMEFRAMES[0];
    // Derive a deterministic id from the (limit_type, timeframe) pair, which mirrors the DB's
    // unique key. A non-deterministic id (e.g. _.uniqueId) makes a freshly-fetched service look
    // "changed" versus the draft clone, surfacing a phantom Save prompt on the Service Edit page.
    this._id = _id || `service_user_limit_${this.limit_type}_${this.timeframe}`;
    this.timeframe_value = timeframe_value || 1;
    this.limit_value = typeof limit_value === 'number' ? limit_value : 0;
  }
  /* eslint-enable camelcase */

  get uuid() {
    return this._id;
  }

  clone() {
    return new ServiceUserLimit(this.serialize());
  }

  serialize() {
    return omitNull({
      _id: this._id,
      limit_type: this.limit_type,
      timeframe: this.timeframe,
      timeframe_value: this.timeframe_value,
      limit_value: this.limit_value,
    });
  }
}

/*
  The service_user_limit table has a unique key on (service_id, limit_type, timeframe), so
  two limits sharing a type and timeframe will fail to save with an uncaught server error.
  Used both to flag duplicate rows in the UI and to block Save before that request goes out.
*/
export function isDuplicateUserLimit(userLimits, idx) {
  const limit = userLimits[idx];
  return _.some(userLimits, (other, otherIdx) =>
    otherIdx !== idx &&
    other.limit_type === limit.limit_type &&
    other.timeframe === limit.timeframe
  );
}

export function hasDuplicateUserLimits(userLimits) {
  return _.some(userLimits, (limit, idx) => isDuplicateUserLimit(userLimits, idx));
}

/* Builds the plain-language summary shown under each row, e.g.
   "Riders can take up to 4 rides per service day." or
   "Block new ride bookings if a rider has more than 2 cancellations in the last 2 hours." */
export function describeUserLimit(userLimit) {
  const limitType = userLimit.limit_type;
  const timeframeValue = userLimit.timeframe_value;
  const limitValue = userLimit.limit_value;
  const unit = TIMEFRAME_UNITS[userLimit.timeframe] || TIMEFRAME_UNITS[TIMEFRAMES[0]];
  const noun = LIMIT_TYPE_NOUNS[limitType] || LIMIT_TYPE_NOUNS[LIMIT_TYPES[0]];
  const unitText = timeframeValue === 1 ? unit.singular : `${timeframeValue} ${unit.plural}`;

  if (limitType === 'rides') {
    const rideNoun = limitValue === 1 ? noun.singular : noun.plural;
    return `Riders can take up to ${limitValue} ${rideNoun} per ${unitText}.`;
  }

  const countNoun = limitValue === 1 ? noun.singular : noun.plural;
  return (
    `Block new ride bookings if a rider has more than ${limitValue} ${countNoun} ` +
    `in the last ${unitText}.`
  );
}

// Rider-facing "Booking Limits" text for one limit. Rides read as an allowance; cancellations/
// no-shows warn what restricts future bookings. Noun is always "ride(s)".
export function describeUserLimitForRider(userLimit) {
  const limitType = userLimit.limit_type;
  const timeframeValue = userLimit.timeframe_value;
  const limitValue = userLimit.limit_value;
  const unit = TIMEFRAME_UNITS[userLimit.timeframe] || TIMEFRAME_UNITS[TIMEFRAMES[0]];
  const unitText = timeframeValue === 1 ? unit.singular : unit.plural;
  const timeframePhrase = `within ${timeframeValue} ${unitText}`;
  const rideNoun = limitValue === 1 ? 'ride' : 'rides';

  if (limitType === 'cancellations') {
    return (
      `Canceling more than ${limitValue} ${rideNoun} ${timeframePhrase} ` +
      'will restrict your ability to book new trips.'
    );
  }
  if (limitType === 'no_shows') {
    return (
      `Missing more than ${limitValue} ${rideNoun} ${timeframePhrase} ` +
      'will restrict your ability to book new trips.'
    );
  }
  return `You can book up to ${limitValue} ${rideNoun} ${timeframePhrase}.`;
}

// Stable key for a limit, matching the DB unique key (limit_type, timeframe). Used to pair a
// configured limit with its usage from the rider limits endpoint.
export function userLimitKey(limit) {
  return `${limit.limit_type}_${limit.timeframe}`;
}

// Progress line shown under each limit: how many rides/cancellations/no-shows the rider has in the
// window before this trip. "before this trip" (not "in the last N days") because the window is
// anchored to the requested pickup time, which may be in the future for advance bookings.
export function describeUserLimitProgressForRider(userLimit, currentUsage) {
  const timeframeValue = userLimit.timeframe_value;
  const unit = TIMEFRAME_UNITS[userLimit.timeframe] || TIMEFRAME_UNITS[TIMEFRAMES[0]];
  const unitText = timeframeValue === 1 ? unit.singular : unit.plural;
  const timeframePhrase = `${timeframeValue} ${unitText}`;
  const pastVerbs = {rides: 'booked', cancellations: 'canceled', no_shows: 'missed'};
  const verb = pastVerbs[userLimit.limit_type] || pastVerbs.rides;

  if (!currentUsage) {
    return `You haven't ${verb} any rides in the ${timeframePhrase} before this trip.`;
  }
  const rideNoun = currentUsage === 1 ? 'ride' : 'rides';
  return `You have ${verb} ${currentUsage} ${rideNoun} in the ${timeframePhrase} before this trip.`;
}



// WEBPACK FOOTER //
// ./src/common/types/ServiceUserLimit.js